import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Info, X } from "lucide-react";
import RegulariseChecksRequestModal from "./RegulariseChecksRequestModal";
import {
  RequestFormFooter,
  RequestHistoryOverlay,
  useEssRequestHistory,
  DS_QUICK_FIELD_CLS,
} from "./RequestHistoryOverlay";
import {
  TaxPlanningQuickRequest,
  resolveFormActionLabel,
} from "../../tax-planning";
import {
  buildLeaveTypeOptions,
  defaultLeaveTypeCode,
  fetchLeaveTypesForForms,
} from "../../../../lib/leaveTypeOptions";
import { useAttendanceCodeConfig } from "../../../../context/attendanceCodeConfigContext";
import {
  attendanceService,
  attendanceStructureService,
  helpdeskService,
} from "../../../../services/api";
import DatePicker from "../../../Common/DatePicker";
import {
  listAttendanceShiftSelectOptions,
  resolveDisplayCurrentShiftName,
  resolveChangeShiftPendingWith,
} from "../../../../lib/attendanceShiftOverride";
import { syncAttendanceShiftsCache } from "../../../../lib/syncAttendancePoliciesCache";
import {
  CHANGE_WO_ACTION_LABEL,
  countFullDayChangeWoInMonth,
  isChangeWoAutoApproved,
  resolveChangeWoPendingWith,
  resolveEmployeeWeeklyOffPolicy,
  validateChangeWoRequest,
} from "../../../../lib/weeklyOffChange";
import { getLocalDayKey } from "../../../../lib/attendancePunchStorage";
import {
  maxDate,
  resolveEmployeeMinDateValue,
  toYmd,
} from "../../../../utils/selectableDateBounds";
import {
  buildEmployeeHolidayMappings,
  buildRestrictedHolidayUsage,
  formatRhOptionLabel,
  getEmployeeRestrictedHolidays,
  rhOptionValue,
} from "../../../../lib/restrictedHolidayAccess";
import {
  resolveLeaveTypeIdFromCode,
  useLeaveTypeBalanceHint,
  yearMonthFromLeaveDate,
} from "../../../../lib/leaveTypeBalanceHint";

function deskFormPortal(node) {
  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
}

/** Shared DatePicker field used across quick-request forms (Leave, Shift, etc.). */
function QuickRequestDateField({
  label,
  value,
  onChange,
  required = false,
  darkMode = false,
  className = "",
  inputClassName = "",
  max,
  min,
}) {
  return (
    <label className={`block text-xs font-medium text-[hsl(var(--ds-muted-fg))] ${className}`}>
      {label}
      <DatePicker
        value={value || ""}
        onChange={(e) => onChange(e?.target?.value ?? "")}
        required={required}
        darkMode={darkMode}
        max={max}
        min={min}
        portal
        dropdownClassName="z-[11100]"
        className={inputClassName || DS_QUICK_FIELD_CLS}
      />
    </label>
  );
}

const QuickRequestFormModal = ({
  darkMode = false,
  isOpen,
  actionLabel,
  context,
  quickActionDraft,
  setQuickActionDraft,
  maxDateToday,
  onClose,
  onSubmitQuickAction,
  onSubmitRegularise,
  onTaxPlanningDeclared,
  isSubmitting = false,
}) => {
  const { mapping: attendanceMapping } = useAttendanceCodeConfig();
  const [companyLeaveTypes, setCompanyLeaveTypes] = useState([]);
  const [leaveTypesLoading, setLeaveTypesLoading] = useState(false);
  const [leaveTypesMessage, setLeaveTypesMessage] = useState(null);
  const [helpdeskMasters, setHelpdeskMasters] = useState({
    ticketTypes: [],
    categories: [],
    priorities: [],
  });
  const [rhCatalog, setRhCatalog] = useState({
    options: [],
    usage: null,
    loading: false,
    error: null,
  });
  const [changeWoLocks, setChangeWoLocks] = useState([]);
  const [changeWoInfoOpen, setChangeWoInfoOpen] = useState(false);

  const formActionLabel = resolveFormActionLabel(actionLabel);
  const isRhForm = formActionLabel === "Restricted Holiday";

  useEffect(() => {
    if (!isOpen || formActionLabel !== CHANGE_WO_ACTION_LABEL) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await attendanceStructureService.getAttendanceLocks();
        const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        if (!cancelled) setChangeWoLocks(rows);
      } catch {
        if (!cancelled) setChangeWoLocks([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, formActionLabel]);

  const employeeMinDateValue = useMemo(() => {
    return (
      resolveEmployeeMinDateValue({
        joiningDate: context?.joiningDate,
        joinDate: context?.joinDate,
        dateOfJoin: context?.dateOfJoin,
        dateOfJoining: context?.dateOfJoining,
      }) || undefined
    );
  }, [
    context?.joiningDate,
    context?.joinDate,
    context?.dateOfJoin,
    context?.dateOfJoining,
  ]);

  const fromDateMin = employeeMinDateValue;
  const toDateMin =
    maxDate(employeeMinDateValue, quickActionDraft?.fromDate) != null
      ? toYmd(maxDate(employeeMinDateValue, quickActionDraft?.fromDate))
      : quickActionDraft?.fromDate || employeeMinDateValue;
  const isLeaveForm = formActionLabel === "Leave";
  const isTicketForm = formActionLabel === "Ticket";
  const isChangeShiftForm = formActionLabel === "Change Shift";
  const leaveEmployeeId = context?.employeeId || null;
  const usesSharedHistory =
    isOpen &&
    !!formActionLabel &&
    formActionLabel !== "Regularise checks" &&
    formActionLabel !== "Tax Planning";
  const requestHistory = useEssRequestHistory({
    enabled: usesSharedHistory,
    actionLabel: formActionLabel,
    employeeId: leaveEmployeeId,
    formFromDate:
      formActionLabel === "On Duty"
        ? quickActionDraft?.date || quickActionDraft?.fromDate || ""
        : null,
    formToDate:
      formActionLabel === "On Duty"
        ? quickActionDraft?.toDate || quickActionDraft?.date || quickActionDraft?.fromDate || ""
        : null,
  });
  const historyEmployeeLabel = context?.employeeCode
    ? `${context.employeeCode} - ${context?.userName || "Employee"}`
    : context?.userName || "Current user";

  const rhYear = useMemo(() => {
    const fromDate = String(quickActionDraft?.date || "").slice(0, 4);
    const n = Number(fromDate);
    if (Number.isFinite(n) && n >= 2000) return n;
    return new Date().getFullYear();
  }, [quickActionDraft?.date]);

  useEffect(() => {
    if (!isOpen || !isRhForm || !leaveEmployeeId) {
      setRhCatalog({ options: [], usage: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setRhCatalog((prev) => ({ ...prev, loading: true, error: null }));
    (async () => {
      try {
        const [policiesRes, mappingRes, listRes] = await Promise.all([
          attendanceStructureService.getHolidayPolicies().catch(() => null),
          attendanceStructureService.getEmployeeHolidayPolicies().catch(() => null),
          attendanceService
            .listRestrictedHolidays({ employeeId: leaveEmployeeId, year: rhYear })
            .catch(() => null),
        ]);
        if (cancelled) return;
        const policies = Array.isArray(policiesRes?.data)
          ? policiesRes.data
          : Array.isArray(policiesRes)
            ? policiesRes
            : [];
        const mappingsRaw = mappingRes?.data ?? mappingRes ?? {};
        const mappings =
          mappingsRaw && !Array.isArray(mappingsRaw)
            ? mappingsRaw
            : buildEmployeeHolidayMappings(mappingsRaw);
        const selections = Array.isArray(listRes?.data)
          ? listRes.data
          : Array.isArray(listRes)
            ? listRes
            : [];
        const allRh = getEmployeeRestrictedHolidays({
          employeeId: leaveEmployeeId,
          holidayPolicies: policies,
          employeeHolidayMappings: mappings,
          year: rhYear,
        });
        const usedKeys = new Set();
        let utilised = 0;
        for (const row of selections) {
          const st = String(row.status || "").toLowerCase();
          if (st !== "pending" && st !== "approved") continue;
          utilised += 1;
          const d = String(row.holidayDate || row.holiday_date || "").slice(0, 10);
          if (d) usedKeys.add(d);
        }
        const available = allRh.filter((h) => {
          const d = String(h.date || "").slice(0, 10);
          return d && !usedKeys.has(d);
        });
        let maxAllowed = null;
        for (const h of allRh) {
          if (h.maxRestrictedHolidaysPerYear != null && h.maxRestrictedHolidaysPerYear !== "") {
            maxAllowed = Number(h.maxRestrictedHolidaysPerYear) || 0;
            break;
          }
        }
        if (maxAllowed == null) {
          for (const p of policies) {
            if (p?.maxRestrictedHolidaysPerYear != null && p.maxRestrictedHolidaysPerYear !== "") {
              maxAllowed = Number(p.maxRestrictedHolidaysPerYear) || 0;
              break;
            }
          }
        }
        const usage = buildRestrictedHolidayUsage({
          year: rhYear,
          maxAllowed,
          utilisedCount: utilised,
        });
        setRhCatalog({
          options: available,
          usage,
          loading: false,
          error: available.length ? null : "No available Restricted Holidays for this year.",
        });
        // Prefill dropdown when form opened from a calendar RH date.
        setQuickActionDraft((p) => {
          if (p.rhKey || p.holidayId) return p;
          const wantDate = String(p.date || "").slice(0, 10);
          if (!wantDate) return p;
          const match = available.find(
            (h) => String(h.date || "").slice(0, 10) === wantDate
          );
          if (!match) return p;
          return {
            ...p,
            rhKey: rhOptionValue(match),
            holidayId: match.id ?? null,
            holidayName: match.name || "",
            date: wantDate,
          };
        });
      } catch (err) {
        if (cancelled) return;
        setRhCatalog({
          options: [],
          usage: null,
          loading: false,
          error: err?.message || "Failed to load Restricted Holidays.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isRhForm, leaveEmployeeId, rhYear]);


  const [shiftCatalogTick, setShiftCatalogTick] = useState(0);

  const shiftSelectOptions = useMemo(() => {
    if (!isOpen || !isChangeShiftForm) return [];
    return listAttendanceShiftSelectOptions();
  }, [isOpen, isChangeShiftForm, shiftCatalogTick]);

  useEffect(() => {
    if (!isOpen || !isChangeShiftForm) return;
    let cancelled = false;
    (async () => {
      const list = await syncAttendanceShiftsCache();
      if (!cancelled && Array.isArray(list) && list.length) {
        setShiftCatalogTick((n) => n + 1);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isChangeShiftForm]);

  useEffect(() => {
    if (!isOpen || !isChangeShiftForm) return;
    const dayKey =
      String(quickActionDraft?.date || "").trim() || getLocalDayKey();
    const resolved = resolveDisplayCurrentShiftName(leaveEmployeeId, dayKey);
    const names = new Set(shiftSelectOptions.map((o) => o.name));
    setQuickActionDraft((p) => {
      const next = { ...p };
      let changed = false;
      if (!String(p.date || "").trim()) {
        next.date = dayKey;
        changed = true;
      }
      const cur = String(p.currentShift || "").trim();
      // Only prefill when empty so the manager/employee can still override.
      if (!cur && resolved) {
        next.currentShift = resolved;
        changed = true;
      } else if (cur && names.size > 0 && !names.has(cur) && resolved && names.has(resolved)) {
        next.currentShift = resolved;
        changed = true;
      }
      return changed ? next : p;
    });
    // Intentionally omit currentShift from deps — only seed when opening / date changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isChangeShiftForm, leaveEmployeeId, quickActionDraft?.date, shiftSelectOptions]);

  useEffect(() => {
    if (!isOpen || !isLeaveForm) return;
    let cancelled = false;
    (async () => {
      setLeaveTypesLoading(true);
      setLeaveTypesMessage(null);
      const result = await fetchLeaveTypesForForms(leaveEmployeeId);
      if (cancelled) return;
      setCompanyLeaveTypes(result.list || []);
      setLeaveTypesMessage(
        result.list?.length
          ? null
          : result.message ||
              (leaveEmployeeId
                ? "No leave types are applicable for this employee (check leave group assignment)."
                : "No leave types configured")
      );
      setLeaveTypesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isLeaveForm, leaveEmployeeId]);

  useEffect(() => {
    if (!isOpen || !isTicketForm) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await helpdeskService.getConfig();
        const masters = res?.data?.masters || {};
        if (cancelled) return;
        setHelpdeskMasters({
          ticketTypes: (masters.ticketTypes || []).filter((t) => t.enabled !== false),
          categories: (masters.categories || []).filter((c) => c.enabled !== false),
          priorities: (masters.priorities || []).filter((p) => p.enabled !== false),
        });
      } catch {
        if (!cancelled) {
          setHelpdeskMasters({ ticketTypes: [], categories: [], priorities: [] });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isTicketForm]);

  const ticketCategories = useMemo(() => {
    const typeId = quickActionDraft?.ticketType;
    if (!typeId) return [];
    return (helpdeskMasters.categories || []).filter((c) => c.typeId === typeId);
  }, [helpdeskMasters.categories, quickActionDraft?.ticketType]);

  const leaveTypeOptions = useMemo(
    () =>
      buildLeaveTypeOptions(companyLeaveTypes, {
        employee: context?.employeeId
          ? { id: context.employeeId, gender: context?.gender }
          : null,
        attendanceMapping,
      }),
    [companyLeaveTypes, attendanceMapping, context?.employeeId, context?.gender]
  );

  const leaveTypeIdForBalance = useMemo(
    () => resolveLeaveTypeIdFromCode(companyLeaveTypes, quickActionDraft?.leaveType),
    [companyLeaveTypes, quickActionDraft?.leaveType]
  );
  const leaveAsOf = yearMonthFromLeaveDate(
    quickActionDraft?.fromDate || quickActionDraft?.date || ""
  );
  const leaveBalanceHint = useLeaveTypeBalanceHint({
    employeeId: leaveEmployeeId,
    leaveTypeId: leaveTypeIdForBalance,
    enabled: Boolean(isOpen && isLeaveForm && leaveTypeIdForBalance),
    year: leaveAsOf.year,
    month: leaveAsOf.month,
    asOfDate: quickActionDraft?.fromDate || quickActionDraft?.date || "",
  });

  useEffect(() => {
    if (!isOpen || !isLeaveForm || leaveTypesLoading) return;
    if (leaveTypeOptions.length === 0) return;
    const current = String(quickActionDraft?.leaveType || "").trim();
    const stillValid = leaveTypeOptions.some((o) => o.code === current);
    if (!stillValid) {
      setQuickActionDraft((p) => ({
        ...p,
        leaveType: defaultLeaveTypeCode(leaveTypeOptions),
      }));
    }
  }, [
    isOpen,
    isLeaveForm,
    leaveTypesLoading,
    leaveTypeOptions,
    quickActionDraft?.leaveType,
    setQuickActionDraft,
  ]);

  if (!isOpen || !actionLabel) return null;

  const dsFieldCls = DS_QUICK_FIELD_CLS;
  const inputCls = DS_QUICK_FIELD_CLS;
  const historyOverlay = (
    <RequestHistoryOverlay
      open={requestHistory.open}
      onClose={() => requestHistory.setOpen(false)}
      title={`${formActionLabel} application history`}
      subtitle={historyEmployeeLabel}
      rows={requestHistory.rows}
      loading={requestHistory.loading}
    />
  );
  const historyFooter = (submitDisabled = false, submitLabel = "Submit Request") => (
    <RequestFormFooter
      historyCount={requestHistory.rows.length}
      historyLoading={requestHistory.loading}
      showHistory={
        formActionLabel !== "On Duty" || requestHistory.rows.length > 0
      }
      onViewHistory={() => requestHistory.setOpen(true)}
      onCancel={onClose}
      isSubmitting={isSubmitting}
      submitDisabled={submitDisabled}
      submitLabel={submitLabel}
    />
  );

  if (formActionLabel === "Regularise checks") {
    return (
      <RegulariseChecksRequestModal
        isOpen
        darkMode={darkMode}
        zIndexClass="z-[11050]"
        employeeId={context?.employeeId}
        employeeName={context?.userName || "Current user"}
        joiningDate={
          context?.joiningDate ||
          context?.joinDate ||
          context?.dateOfJoin ||
          context?.dateOfJoining ||
          null
        }
        initialDateIso={quickActionDraft.date || maxDateToday}
        onClose={onClose}
        onSubmit={onSubmitRegularise}
      />
    );
  }

  if (formActionLabel === "Tax Planning") {
    return (
      <TaxPlanningQuickRequest
        context={context}
        onClose={onClose}
        onDeclared={(data) => {
          onTaxPlanningDeclared?.(data);
        }}
      />
    );
  }

  if (formActionLabel === "Change Shift") {
    const pendingHint = resolveChangeShiftPendingWith(quickActionDraft?.shift);
    return deskFormPortal(
      <>
      <div className="fixed inset-0 z-[11050] flex items-center justify-center bg-black/45 p-4 pt-20 sm:pt-4">
        <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)]">
          <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-5 py-4">
            <div>
              <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">Change Shift</h3>
              <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
                {context?.employeeCode
                  ? `${context.employeeCode} - ${context?.userName || "Employee"}`
                  : `Employee: ${context?.userName || "Current user"}`}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <form
            onSubmit={onSubmitQuickAction}
            className="space-y-4 overflow-y-auto px-5 py-4"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <QuickRequestDateField
                label="Effective Date"
                required
                darkMode={darkMode}
                value={quickActionDraft.date}
                min={employeeMinDateValue}
                onChange={(date) => setQuickActionDraft((p) => ({ ...p, date }))}
                inputClassName={dsFieldCls}
              />
              <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                Current Shift
                <select
                  value={quickActionDraft.currentShift || ""}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, currentShift: e.target.value }))
                  }
                  className={dsFieldCls}
                >
                  <option value="">Select current shift</option>
                  {shiftSelectOptions.map((o) => (
                    <option key={`cur-${o.id}`} value={o.name}>
                      {o.label}
                    </option>
                  ))}
                  {quickActionDraft.currentShift &&
                    !shiftSelectOptions.some(
                      (o) => o.name === quickActionDraft.currentShift
                    ) && (
                      <option value={quickActionDraft.currentShift}>
                        {quickActionDraft.currentShift} (not in catalog)
                      </option>
                    )}
                </select>
              </label>
              <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                New Shift
                <select
                  required
                  value={quickActionDraft.shift || ""}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, shift: e.target.value }))
                  }
                  className={dsFieldCls}
                >
                  <option value="">Select new shift</option>
                  {shiftSelectOptions.map((o) => (
                    <option key={`new-${o.id}`} value={o.name}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {shiftSelectOptions.length === 0 && (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
                No shifts found. Add them under Attendance → Shifts, then reopen this form.
              </p>
            )}

            <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              Reason
              <textarea
                required
                rows={3}
                value={quickActionDraft.reason}
                onChange={(e) =>
                  setQuickActionDraft((p) => ({ ...p, reason: e.target.value }))
                }
                className={`${dsFieldCls} resize-y`}
                placeholder="Why is the shift changing?"
              />
            </label>
            <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              Notes (optional)
              <textarea
                rows={2}
                value={quickActionDraft.notes}
                onChange={(e) =>
                  setQuickActionDraft((p) => ({ ...p, notes: e.target.value }))
                }
                className={`${dsFieldCls} resize-y`}
              />
            </label>

            <p className="rounded-xl bg-[hsl(var(--ds-muted))] px-3 py-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              After submit, this stays <span className="font-semibold">Pending</span> with{" "}
              <span className="font-semibold">{pendingHint}</span> until approved on Approvals.
              Once approved, attendance eval uses the new shift from the effective date.
            </p>

            {historyFooter(isSubmitting || shiftSelectOptions.length === 0)}
          </form>
        </div>
      </div>
      {historyOverlay}
      </>
    );
  }

  if (formActionLabel === CHANGE_WO_ACTION_LABEL) {
    const employeeStub = leaveEmployeeId ? { id: leaveEmployeeId } : null;
    const policy = resolveEmployeeWeeklyOffPolicy(employeeStub);
    const dayKey =
      String(quickActionDraft?.date || "").trim() || getLocalDayKey();
    const proposedKey = String(quickActionDraft?.proposedDate || "").trim();
    const locksForValidate = Array.isArray(context?.attendanceLocks)
      ? context.attendanceLocks
      : changeWoLocks;
    const validation = employeeStub
      ? validateChangeWoRequest({
          employee: employeeStub,
          date: dayKey,
          proposedDate: proposedKey,
          attendanceLocks: locksForValidate,
        })
      : { ok: false, message: "Employee required." };
    const used = leaveEmployeeId
      ? countFullDayChangeWoInMonth(leaveEmployeeId, dayKey.slice(0, 7))
      : 0;
    const cap = policy?.maxFullDayWoChangePerMonth;
    const auto = policy ? isChangeWoAutoApproved(policy) : false;
    const pendingHint = auto
      ? "Auto-approved"
      : resolveChangeWoPendingWith(policy) || "Reporting Manager";

    const infoLines = [
      "After approval, Current WO becomes a working day and Proposed WO becomes the new Weekly Off.",
      cap != null && Number.isFinite(Number(cap))
        ? `Monthly cap: ${used} / ${cap} used this month.`
        : null,
      "Locked or processed attendance months cannot be changed. Unprocess or unlock first.",
      auto
        ? "Policy is set to auto-approve. Submit applies the swap immediately."
        : `After submit, this stays Pending with ${pendingHint} until approved.`,
    ].filter(Boolean);

    return deskFormPortal(
      <>
      <div className="fixed inset-0 z-[11050] flex items-center justify-center bg-black/45 p-4 pt-20 sm:pt-4">
        <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)]">
          <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-5 py-4">
            <div>
              <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">Change WO</h3>
              <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
                {context?.employeeCode
                  ? `${context.employeeCode} - ${context?.userName || "Employee"}`
                  : `Employee: ${context?.userName || "Current user"}`}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setChangeWoInfoOpen((open) => !open);
                  }}
                  onBlur={() => setChangeWoInfoOpen(false)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
                  aria-label="Change WO details"
                  aria-expanded={changeWoInfoOpen}
                >
                  <Info size={18} strokeWidth={1.75} />
                </button>
                {changeWoInfoOpen ? (
                  <div
                    className="absolute right-0 top-full z-20 mt-1 w-72 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3 text-left text-xs leading-snug text-[hsl(var(--ds-muted-fg))] shadow-lg sm:w-80"
                    role="tooltip"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <ul className="space-y-2">
                      {infoLines.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <form
            onSubmit={onSubmitQuickAction}
            className="space-y-4 overflow-y-auto px-5 py-4"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-end">
              <QuickRequestDateField
                label="Current WO"
                required
                darkMode={darkMode}
                value={quickActionDraft.date}
                min={employeeMinDateValue}
                onChange={(date) => setQuickActionDraft((p) => ({ ...p, date }))}
                inputClassName={dsFieldCls}
              />
              <QuickRequestDateField
                label="Proposed WO"
                required
                darkMode={darkMode}
                value={quickActionDraft.proposedDate || ""}
                min={employeeMinDateValue}
                onChange={(proposedDate) =>
                  setQuickActionDraft((p) => ({ ...p, proposedDate }))
                }
                inputClassName={dsFieldCls}
              />
            </div>

            {dayKey && proposedKey && !validation.ok && (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
                {validation.message}
              </p>
            )}

            <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              Reason
              <textarea
                required
                rows={3}
                value={quickActionDraft.reason}
                onChange={(e) =>
                  setQuickActionDraft((p) => ({ ...p, reason: e.target.value }))
                }
                className={`${dsFieldCls} resize-y`}
                placeholder="Why are you moving this Weekly Off?"
              />
            </label>
            <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              Notes (optional)
              <textarea
                rows={2}
                value={quickActionDraft.notes}
                onChange={(e) =>
                  setQuickActionDraft((p) => ({ ...p, notes: e.target.value }))
                }
                className={`${dsFieldCls} resize-y`}
              />
            </label>

            {historyFooter(isSubmitting || !validation.ok)}
          </form>
        </div>
      </div>
      {historyOverlay}
      </>
    );
  }

  if (formActionLabel === "Leave") {
    return deskFormPortal(
      <>
      <div className="fixed inset-0 z-[11050] flex items-center justify-center bg-black/45 p-4 pt-20 sm:pt-4">
        <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)]">
          <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-5 py-4">
            <div>
              <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">Leave</h3>
              <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
                {context?.employeeCode
                  ? `${context.employeeCode} - ${context?.userName || "Employee"}`
                  : `Employee: ${context?.userName || "Current user"}`}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <form
            onSubmit={onSubmitQuickAction}
            className="space-y-4 overflow-y-auto px-5 py-4"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <div className="flex items-center justify-between gap-2">
                  <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                    Leave Type <span className="text-[hsl(var(--ds-danger))]">*</span>
                  </label>
                  {quickActionDraft.leaveType ? (
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                        leaveBalanceHint.loading
                          ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                          : leaveBalanceHint.label
                            ? "bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-primary))]"
                            : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                      }`}
                    >
                      {leaveBalanceHint.loading
                        ? "Balance…"
                        : leaveBalanceHint.label || "No balance data"}
                    </span>
                  ) : null}
                </div>
                <select
                  value={quickActionDraft.leaveType}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, leaveType: e.target.value }))
                  }
                  className={dsFieldCls}
                  disabled={leaveTypesLoading || leaveTypeOptions.length === 0}
                  required
                >
                  {leaveTypesLoading && (
                    <option value="">Loading leave types…</option>
                  )}
                  {!leaveTypesLoading && leaveTypeOptions.length === 0 && (
                    <option value="">
                      {leaveTypesMessage || "No applicable leave types"}
                    </option>
                  )}
                  {leaveTypeOptions.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.code}
                      {opt.label && opt.label !== opt.code ? ` — ${opt.label}` : ""}
                    </option>
                  ))}
                </select>
                {!leaveTypesLoading && leaveTypesMessage && leaveTypeOptions.length === 0 && (
                  <span className="mt-1 block text-[11px] text-amber-700 dark:text-amber-300">
                    {leaveTypesMessage}
                  </span>
                )}
              </div>
              <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                Leave Duration
                <select
                  value={quickActionDraft.leaveDuration}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, leaveDuration: e.target.value }))
                  }
                  className={dsFieldCls}
                >
                  <option value="full_day">Full Day</option>
                  <option value="first_half">First Half</option>
                  <option value="second_half">Second Half</option>
                </select>
              </label>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <QuickRequestDateField
                label="From Date"
                required
                darkMode={darkMode}
                value={quickActionDraft.fromDate}
                min={fromDateMin}
                onChange={(fromDate) =>
                  setQuickActionDraft((p) => ({ ...p, fromDate }))
                }
                inputClassName={dsFieldCls}
              />
              <QuickRequestDateField
                label="To Date"
                required
                darkMode={darkMode}
                value={quickActionDraft.toDate}
                min={toDateMin}
                onChange={(toDate) =>
                  setQuickActionDraft((p) => ({ ...p, toDate }))
                }
                inputClassName={dsFieldCls}
              />
            </div>

            <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              Reason
              <textarea
                required
                rows={3}
                value={quickActionDraft.reason || ""}
                onChange={(e) =>
                  setQuickActionDraft((p) => ({ ...p, reason: e.target.value }))
                }
                className={`${dsFieldCls} resize-y`}
                placeholder="Reason for leave"
              />
            </label>

            <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              Notes (optional)
              <textarea
                rows={2}
                value={quickActionDraft.notes || ""}
                onChange={(e) =>
                  setQuickActionDraft((p) => ({ ...p, notes: e.target.value }))
                }
                className={`${dsFieldCls} resize-y`}
              />
            </label>

            {historyFooter(
              isSubmitting || leaveTypesLoading || leaveTypeOptions.length === 0
            )}
          </form>
        </div>
      </div>
      {historyOverlay}
      </>
    );
  }

  if (formActionLabel === "Restricted Holiday") {
    const selectValue =
      quickActionDraft.rhKey ||
      (quickActionDraft.holidayId ? `id:${quickActionDraft.holidayId}` : "");
    return deskFormPortal(
      <>
      <div className="fixed inset-0 z-[11050] flex items-center justify-center bg-black/45 p-4 pt-20 sm:pt-4">
        <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)]">
          <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-5 py-4">
            <div>
              <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">Restricted Holiday</h3>
              <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
                {context?.employeeCode
                  ? `${context.employeeCode} - ${context?.userName || "Employee"}`
                  : `Employee: ${context?.userName || "Current user"}`}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <form
            onSubmit={onSubmitQuickAction}
            className="space-y-4 overflow-y-auto px-5 py-4"
          >
            {rhCatalog.usage?.label ? (
              <p className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 px-3 py-2 text-xs text-[hsl(var(--ds-fg))]">
                {rhCatalog.usage.label}
              </p>
            ) : null}

            <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              Holiday <span className="text-[hsl(var(--ds-danger))]">*</span>
              <select
                required
                disabled={rhCatalog.loading || !rhCatalog.options.length}
                value={selectValue}
                onChange={(e) => {
                  const key = e.target.value;
                  const selected = rhCatalog.options.find(
                    (h) => rhOptionValue(h) === key
                  );
                  setQuickActionDraft((p) => ({
                    ...p,
                    rhKey: key,
                    holidayId: selected?.id ?? null,
                    holidayName: selected?.name || "",
                    date: selected?.date
                      ? String(selected.date).slice(0, 10)
                      : p.date,
                  }));
                }}
                className={dsFieldCls}
              >
                <option value="">
                  {rhCatalog.loading
                    ? "Loading holidays…"
                    : "Select from available RHs"}
                </option>
                {rhCatalog.options.map((h) => (
                  <option key={rhOptionValue(h)} value={rhOptionValue(h)}>
                    {formatRhOptionLabel(h)}
                  </option>
                ))}
              </select>
            </label>

            {rhCatalog.error ? (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
                {rhCatalog.error}
              </p>
            ) : (
              <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Date is taken from the selected Restricted Holiday on your calendar.
                {quickActionDraft.date
                  ? ` Selected date: ${String(quickActionDraft.date).slice(0, 10)}.`
                  : ""}
              </p>
            )}

            <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              Reason <span className="text-[hsl(var(--ds-danger))]">*</span>
              <textarea
                required
                rows={3}
                value={quickActionDraft.reason || ""}
                onChange={(e) =>
                  setQuickActionDraft((p) => ({ ...p, reason: e.target.value }))
                }
                className={`${dsFieldCls} resize-y`}
                placeholder="Reason for applying this Restricted Holiday"
              />
            </label>

            <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              Notes (optional)
              <textarea
                rows={2}
                value={quickActionDraft.notes || ""}
                onChange={(e) =>
                  setQuickActionDraft((p) => ({ ...p, notes: e.target.value }))
                }
                className={`${dsFieldCls} resize-y`}
              />
            </label>

            <p className="rounded-xl bg-[hsl(var(--ds-muted))] px-3 py-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              After submit, this stays <span className="font-semibold">Pending</span> with{" "}
              <span className="font-semibold">Reporting Manager</span> until approved on
              Approvals.
            </p>

            {historyFooter(
              isSubmitting ||
                rhCatalog.loading ||
                !rhCatalog.options.length ||
                !selectValue
            )}
          </form>
        </div>
      </div>
      {historyOverlay}
      </>
    );
  }

  return deskFormPortal(
    <>
    <div className="fixed inset-0 z-[11050] flex items-center justify-center bg-black/45 p-4 pt-20 sm:pt-4">
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)]">
        <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-5 py-4">
          <div>
            <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">
              {actionLabel}
            </h3>
            <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
              {context?.employeeCode
                ? `${context.employeeCode} - ${context?.userName || "Employee"}`
                : `Employee: ${context?.userName || "Current user"}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmitQuickAction} className="space-y-4 overflow-y-auto px-5 py-4">
          {formActionLabel === "Short Leave" && (
            <div className="grid grid-cols-3 gap-3">
              <QuickRequestDateField
                label="Date"
                required
                darkMode={darkMode}
                value={quickActionDraft.date}
                min={employeeMinDateValue}
                onChange={(date) => setQuickActionDraft((p) => ({ ...p, date }))}
              />
              <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                Start Time
                <input
                  type="time"
                  required
                  value={quickActionDraft.startTime}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, startTime: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
              <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                End Time
                <input
                  type="time"
                  required
                  value={quickActionDraft.endTime}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, endTime: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
            </div>
          )}

          {(formActionLabel === "On Duty" ||
            formActionLabel === "Comp Off" ||
            formActionLabel === "Overtime") && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <QuickRequestDateField
                label="Date"
                required
                darkMode={darkMode}
                value={quickActionDraft.date}
                min={employeeMinDateValue}
                onChange={(date) => setQuickActionDraft((p) => ({ ...p, date }))}
              />
              {formActionLabel === "Overtime" && (
                <>
                  <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                    Start Time
                    <input
                      type="time"
                      required
                      value={quickActionDraft.startTime}
                      onChange={(e) =>
                        setQuickActionDraft((p) => ({ ...p, startTime: e.target.value }))
                      }
                      className={inputCls}
                    />
                  </label>
                  <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                    End Time
                    <input
                      type="time"
                      required
                      value={quickActionDraft.endTime}
                      onChange={(e) =>
                        setQuickActionDraft((p) => ({ ...p, endTime: e.target.value }))
                      }
                      className={inputCls}
                    />
                  </label>
                </>
              )}
              {formActionLabel === "On Duty" && (
                <QuickRequestDateField
                  label="To Date (optional)"
                  darkMode={darkMode}
                  value={quickActionDraft.toDate}
                  min={
                    toYmd(
                      maxDate(employeeMinDateValue, quickActionDraft.date)
                    ) ||
                    quickActionDraft.date ||
                    employeeMinDateValue
                  }
                  onChange={(toDate) => setQuickActionDraft((p) => ({ ...p, toDate }))}
                />
              )}
            </div>
          )}

          {formActionLabel === "Resignation" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <QuickRequestDateField
                  label="Resignation Date"
                  required
                  darkMode={darkMode}
                  value={quickActionDraft.resignationDate}
                  min={employeeMinDateValue}
                  onChange={(resignationDate) =>
                    setQuickActionDraft((p) => ({ ...p, resignationDate }))
                  }
                />
                <QuickRequestDateField
                  label="Last Working Date"
                  required
                  darkMode={darkMode}
                  value={quickActionDraft.lastWorkingDate}
                  min={
                    toYmd(
                      maxDate(
                        employeeMinDateValue,
                        quickActionDraft.resignationDate
                      )
                    ) ||
                    quickActionDraft.resignationDate ||
                    employeeMinDateValue
                  }
                  onChange={(lastWorkingDate) =>
                    setQuickActionDraft((p) => ({ ...p, lastWorkingDate }))
                  }
                />
              </div>
              <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                Resignation Reason
                <select
                  required
                  value={quickActionDraft.resignationReason}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, resignationReason: e.target.value }))
                  }
                  className={inputCls}
                >
                  <option value="">Select reason</option>
                  <option value="career_growth">Career Growth</option>
                  <option value="personal_reason">Personal Reason</option>
                  <option value="higher_studies">Higher Studies</option>
                  <option value="relocation">Relocation</option>
                  <option value="health">Health</option>
                  <option value="other">Other</option>
                </select>
              </label>
            </>
          )}

          {formActionLabel === "Ticket" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                  Ticket Type
                  <select
                    value={quickActionDraft.ticketType}
                    onChange={(e) => {
                      const ticketType = e.target.value;
                      const cats = (helpdeskMasters.categories || []).filter(
                        (c) => c.typeId === ticketType
                      );
                      const firstCat = cats[0];
                      setQuickActionDraft((p) => ({
                        ...p,
                        ticketType,
                        ticketCategory: firstCat?.id || "",
                        ticketSubcategory: "",
                      }));
                    }}
                    className={inputCls}
                    required
                  >
                    <option value="">Select type</option>
                    {helpdeskMasters.ticketTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                  Priority
                  <select
                    value={quickActionDraft.ticketPriority}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, ticketPriority: e.target.value }))
                    }
                    className={inputCls}
                  >
                    {helpdeskMasters.priorities.length ? (
                      helpdeskMasters.priorities.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </>
                    )}
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                  Category
                  <select
                    value={quickActionDraft.ticketCategory || ""}
                    onChange={(e) => {
                      setQuickActionDraft((p) => ({
                        ...p,
                        ticketCategory: e.target.value,
                        ticketSubcategory: "",
                      }));
                    }}
                    className={inputCls}
                    disabled={!quickActionDraft.ticketType}
                    required
                  >
                    <option value="">
                      {quickActionDraft.ticketType ? "Select category" : "Select type first"}
                    </option>
                    {ticketCategories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                Subject
                <input
                  type="text"
                  value={quickActionDraft.ticketSubject}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, ticketSubject: e.target.value }))
                  }
                  className={inputCls}
                  required
                />
              </label>
            </>
          )}

          {formActionLabel === "MRF" && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                Department
                <input
                  type="text"
                  value={quickActionDraft.requestDepartment}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, requestDepartment: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
              <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                Designation
                <input
                  type="text"
                  value={quickActionDraft.requestDesignation}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, requestDesignation: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
            </div>
          )}

          <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
            Reason
            <textarea
              required
              rows={3}
              value={quickActionDraft.reason}
              onChange={(e) =>
                setQuickActionDraft((p) => ({ ...p, reason: e.target.value }))
              }
              className={inputCls}
            />
          </label>
          <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
            Notes (optional)
            <textarea
              rows={2}
              value={quickActionDraft.notes}
              onChange={(e) =>
                setQuickActionDraft((p) => ({ ...p, notes: e.target.value }))
              }
              className={inputCls}
            />
          </label>

          {historyFooter(isSubmitting)}
        </form>
      </div>
    </div>
    {historyOverlay}
    </>
  );
};

export default QuickRequestFormModal;
